import { useState, useEffect, useRef } from 'react';
import Sidebar from '../home/components/Sidebar';
import Navbar from '../home/components/Navbar';
import { getCurrentUser } from '../../services/auth';
import { deductCredits } from '../../services/credits';
import { createHistory, updateHistoryByTaskId } from '../../services/history';
import {
  getVideoModelConfig,
  createVideoTask,
  getVideoTaskStatus,
  getVideoUrlFromTaskResult,
  type VideoModelConfig,
} from '../../services/video';

/** 单次生成消耗积分（首尾帧视频） */
const VEO31_CREDITS_PER_USE = 30;

export default function Veo31VideoPage() {
  const [activeMenu, setActiveMenu] = useState('首尾帧视频');
  const [aspectRatio, setAspectRatio] = useState('竖屏 9:16');
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [firstFrameImage, setFirstFrameImage] = useState<string | null>(null);
  const [lastFrameImage, setLastFrameImage] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [user, setUser] = useState<Awaited<ReturnType<typeof getCurrentUser>>>(null);
  const [videoModelConfig, setVideoModelConfig] = useState<VideoModelConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [videoTaskId, setVideoTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 随机提示词库
  const inspirationPrompts = [
    '黄昏的海滩边，浪花在金色的阳光下翻滚，一位穿着白色连衣裙的女孩在沙滩上奔跑，镜头从全景缓慢推进到中景，展现她愉悦的笑容和飘逸的长发。',
    '繁华的都市夜景，霓虹灯闪烁，镜头从高空俯瞰缓缓下降，聚焦在街道上行走的人群，展现城市的活力与繁忙。',
    '清晨的森林中，阳光透过树叶洒下斑驳的光影，一只小鹿在林间漫步，镜头跟随它的脚步，展现大自然的宁静与美好。',
    '咖啡馆内，一位年轻人坐在窗边，手捧咖啡杯凝视窗外，镜头从侧面特写他的表情，窗外是繁忙的街道，形成动静对比。',
    '雪山之巅，登山者站在山顶眺望远方，镜头360度环绕拍摄，展现壮丽的雪山全景和登山者的成就感。',
    '夜晚的图书馆，一盏台灯照亮书桌，镜头从书本特写缓缓拉远，展现安静的学习氛围和专注的读者。',
    '春天的樱花树下，花瓣随风飘落，一对情侣手牵手漫步，镜头从背后跟拍，营造浪漫温馨的氛围。',
    '现代科技实验室，机械臂精准操作，镜头特写展现高科技设备的细节，配合蓝色科技光效，展现未来感。'
  ];

  // 加载用户与视频模型配置（Seedance 首尾帧）
  useEffect(() => {
    const load = async () => {
      const [currentUser, modelConfig] = await Promise.all([
        getCurrentUser(),
        getVideoModelConfig(),
      ]);
      setUser(currentUser);
      setVideoModelConfig(modelConfig ?? null);
    };
    load();
  }, []);

  const checkTaskStatus = async (taskId: string) => {
    if (!videoModelConfig?.api_key) return null;
    try {
      const taskData = await getVideoTaskStatus(videoModelConfig, taskId);
      if (!taskData) return null;
      setTaskStatus(taskData.status ?? null);
      switch (taskData.status) {
        case 'queued':
          setProgress(20);
          break;
        case 'running':
          setProgress((p) => Math.min(p + 10, 90));
          break;
        case 'succeeded': {
          setProgress(100);
          const videoUrl = getVideoUrlFromTaskResult(taskData);
          if (videoUrl) setGeneratedVideoUrl(videoUrl);
          stopPolling();
          if (user && videoUrl) {
            await updateHistoryByTaskId(user.id, taskId, { status: 'succeeded', output_url: videoUrl });
          }
          break;
        }
        case 'failed':
        case 'expired':
          setProgress(0);
          stopPolling();
          if (user) {
            await updateHistoryByTaskId(user.id, taskId, { status: taskData.status });
          }
          break;
        default:
          break;
      }
      return taskData;
    } catch (e) {
      console.error('查询任务状态失败', e);
      return null;
    }
  };

  const startPolling = () => {
    if (!videoTaskId) return;
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    checkTaskStatus(videoTaskId);
    pollingIntervalRef.current = setInterval(() => checkTaskStatus(videoTaskId), 3000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  useEffect(() => () => { stopPolling(); }, []);

  const handleGenerate = async () => {
    setError(null);
    if (!prompt.trim()) {
      setError('请输入描述词');
      return;
    }
    if (!user) {
      setError('请先登录');
      return;
    }
    if (!videoModelConfig?.model_name) {
      setError('视频模型未配置或未启用，请在后台配置并启用视频模型');
      return;
    }
    if (videoModelConfig.provider !== 'alibaba' && !videoModelConfig.api_key) {
      setError('当前视频模型缺少可用密钥。请启用阿里视频模型，或重新配置火山引擎/钱多多密钥');
      return;
    }

    setIsGenerating(true);
    try {
      const deductResult = await deductCredits(
        user.id,
        VEO31_CREDITS_PER_USE,
        '使用首尾帧视频生成（Seedance）'
      );
      if (!deductResult.success) {
        setError(deductResult.error || '积分不足，请前往充值');
        setIsGenerating(false);
        return;
      }

      const ratioValue = aspectRatio === '横屏 16:9' ? '16:9' : '9:16';
      const durationNum = 5; // 首尾帧通常 5 秒

      const taskResult = await createVideoTask(videoModelConfig, {
        prompt: prompt.trim(),
        duration: durationNum,
        ratio: ratioValue,
        imageUrl: firstFrameImage || undefined,
        lastFrameUrl: lastFrameImage || undefined,
        camera_fixed: false,
      });

      if (taskResult?.id) {
        await createHistory(user.id, {
          type: 'video',
          category: 'veo31-video',
          status: 'pending',
          task_id: taskResult.id,
          prompt: prompt.trim(),
          input_images: [firstFrameImage, lastFrameImage].filter(Boolean) as string[],
          settings: { duration: durationNum, ratio: ratioValue, firstFrame: !!firstFrameImage, lastFrame: !!lastFrameImage },
          credits_used: VEO31_CREDITS_PER_USE,
        });
        setVideoTaskId(taskResult.id);
        setProgress(0);
        setTaskStatus('queued');
        setGeneratedVideoUrl(null);
        setShowSuccessModal(true);
      } else {
        setError('视频任务创建失败，请稍后重试');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '视频生成失败');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    startPolling();
  };

  const handleGenerateInspiration = () => {
    const randomPrompt = inspirationPrompts[Math.floor(Math.random() * inspirationPrompts.length)];
    setPrompt(randomPrompt);
  };

  const handleFirstFrameUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFirstFrameImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLastFrameUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLastFrameImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden">
          <div className="mx-auto max-w-7xl transition-all duration-300">
            <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
              {/* 左侧主内容区 */}
              <div className="lg:col-span-2">
                {/* Header */}
                <div className="mb-8">
                  <h1 className="text-3xl font-bold mb-2">首尾帧视频</h1>
                  <p className="text-gray-400 text-sm">
                    可选上传首帧/尾帧参考图，也可以直接输入描述词，Seedance 首尾帧能力可生成高质量视频效果
                  </p>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-400 shrink-0"></i>
                    <p className="text-red-400 text-sm">{error}</p>
                    <button type="button" onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-white">
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                )}

                {videoTaskId && (
                  <div className="bg-[#1a1a1a] border border-gray-800 rounded-2xl p-6 mb-6">
                    <h3 className="text-lg font-semibold mb-4">视频生成进度</h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm text-gray-400">
                        <span>状态：{taskStatus === 'succeeded' ? '已完成' : taskStatus === 'failed' || taskStatus === 'expired' ? '失败/已过期' : '处理中'}</span>
                        <span>{progress}%</span>
                      </div>
                      <div className="h-2 bg-[#0a0a0a] rounded-full overflow-hidden">
                        <div className="h-full bg-blue-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                    {generatedVideoUrl && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-400 mb-2">生成结果：</p>
                        <video src={generatedVideoUrl} controls className="max-w-full rounded-lg max-h-80" />
                      </div>
                    )}
                  </div>
                )}

                {/* Upload Section - 首尾帧 */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-upload-cloud-line text-blue-400 text-xl"></i>
                    <h3 className="text-lg font-semibold">参考图（可选）</h3>
                  </div>
                  <p className="text-gray-500 text-xs mb-4">上传首帧与尾帧参考图，系统将根据首尾帧与描述词生成中间过渡视频</p>

                  {/* Upload Areas */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 flex flex-col items-center justify-center hover:border-blue-500 transition-colors cursor-pointer relative">
                      {firstFrameImage ? (
                        <div className="relative w-full">
                          <img src={firstFrameImage} alt="首帧预览" className="w-full h-48 object-cover rounded-lg" />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFirstFrameImage(null);
                            }}
                            className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line text-white"></i>
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFirstFrameUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <i className="ri-upload-cloud-2-line text-4xl text-gray-600 mb-3"></i>
                          <p className="text-gray-400 text-sm mb-1">上传首帧参考图（推荐）</p>
                          <p className="text-gray-600 text-xs">拖拽图片到此或点击上传，可不上传</p>
                          <p className="text-gray-600 text-xs mt-1">支持 JPG、PNG 格式，建议不超过 5MB</p>
                        </>
                      )}
                    </div>

                    <div className="border-2 border-dashed border-gray-700 rounded-xl p-8 flex flex-col items-center justify-center hover:border-blue-500 transition-colors cursor-pointer relative">
                      {lastFrameImage ? (
                        <div className="relative w-full">
                          <img src={lastFrameImage} alt="尾帧预览" className="w-full h-48 object-cover rounded-lg" />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLastFrameImage(null);
                            }}
                            className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                          >
                            <i className="ri-close-line text-white"></i>
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLastFrameUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          />
                          <i className="ri-upload-cloud-2-line text-4xl text-gray-600 mb-3"></i>
                          <p className="text-gray-400 text-sm mb-1">上传尾帧参考图（可选）</p>
                          <p className="text-gray-600 text-xs">拖拽图片到此或点击上传，可不上传</p>
                          <p className="text-gray-600 text-xs mt-1">支持 JPG、PNG 格式，建议不超过 5MB</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Prompt Section */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-quill-pen-line text-purple-400 text-xl"></i>
                      <h3 className="text-lg font-semibold">提示词</h3>
                    </div>
                    <button 
                      onClick={handleGenerateInspiration}
                      className="text-blue-400 text-sm hover:text-blue-300 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                    >
                      <i className="ri-magic-line"></i>
                      <span>输入灵感</span>
                    </button>
                  </div>

                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="示例：雪山之巅，登山者站在山顶眺望远方，镜头360度环绕拍摄，展现壮丽的雪山全景和登山者的成就感。"
                    className="w-full bg-[#0a0a0a] border border-gray-800 rounded-xl p-4 text-gray-300 text-sm resize-none focus:outline-none focus:border-blue-500 transition-colors"
                    rows={6}
                    maxLength={500}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-gray-600 text-xs">请输入详细的视频描述，包括镜头、场景、动作等信息</p>
                    <p className="text-gray-600 text-xs">{prompt.length}/500</p>
                  </div>
                </div>

                {/* 视频尺寸：单行自适应宽度 */}
                <div className="mb-6 w-full">
                  <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 w-full">
                    <div className="flex items-center gap-2 mb-4">
                      <i className="ri-aspect-ratio-line text-green-400 text-xl"></i>
                      <h3 className="text-lg font-semibold">视频尺寸</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-4 w-full">
                      <button
                        onClick={() => setAspectRatio('竖屏 9:16')}
                        className={`w-full py-4 rounded-lg font-medium transition-all cursor-pointer ${
                          aspectRatio === '竖屏 9:16'
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-400 hover:text-gray-300'
                        }`}
                      >
                        竖屏 9:16
                      </button>
                      <button
                        onClick={() => setAspectRatio('横屏 16:9')}
                        className={`w-full py-4 rounded-lg font-medium transition-all cursor-pointer ${
                          aspectRatio === '横屏 16:9'
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-400 hover:text-gray-300'
                        }`}
                      >
                        横屏 16:9
                      </button>
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating || !prompt.trim()}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                    isGenerating || !prompt.trim()
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>立即生成</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-play-circle-line"></i>
                      <span>立即生成</span>
                      <span className="ml-2 text-sm bg-orange-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                        <i className="ri-flashlight-fill text-xs"></i>
                        {VEO31_CREDITS_PER_USE} 点
                      </span>
                    </>
                  )}
                </button>
              </div>

              {/* 右侧温馨提示区域 */}
              <div className="lg:col-span-1">
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 sticky top-6">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-information-line text-blue-400 text-xl"></i>
                    <h3 className="text-lg font-semibold">温馨提示</h3>
                  </div>
                  <ul className="text-gray-400 text-sm space-y-3">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-blue-400 mt-0.5 shrink-0"></i>
                      <span>建议搭配首帧参考图使用，但也可以直接输入描述词，系统会根据描述词生成视频。</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-blue-400 mt-0.5 shrink-0"></i>
                      <span>描述词需包含镜头、场景、动作等信息，可以让首尾帧输出更符合预期的效果。</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-blue-400 mt-0.5 shrink-0"></i>
                      <span>上传首帧与尾帧参考图即可，系统将根据首尾帧与描述词生成中间过渡视频。</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 max-w-md w-full border border-gray-800 relative">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-check-line text-white text-3xl"></i>
              </div>
              
              <h2 className="text-2xl font-bold mb-3">提交成功</h2>
              
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                任务已提交成功，约5分钟后可在素材空间或历史记录中查看生成结果。
              </p>

              <button
                onClick={handleSuccessConfirm}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 cursor-pointer whitespace-nowrap"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #0a0a0a;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>
    </div>
  );
}
